/**
 * Scroll-driven animations for img + html5video elements with the sidebar
 * "Scroll Animation" section enabled.
 *
 * Per element:
 *   - data-lay-scroll-anim                  → animation type (currently 'scale')
 *   - data-lay-scroll-anim-scale-factor     → starting scale (e.g. 0.9)
 *   - data-lay-scroll-anim-end-at-top       → 0–100, % from viewport bottom
 *                                             going up where the animation
 *                                             completes (e.g. 90 → element top
 *                                             at 10% from viewport top)
 *   - data-lay-scroll-anim-easing           → anime.js-style easing name
 *
 * Progress is recomputed live each frame, so scrolling back up reverses the
 * animation. The rAF loop only ticks while at least one element is
 * intersecting the viewport (IntersectionObserver gates membership), so a page
 * with hundreds of decorated images stays cheap when none are visible.
 */

import { getProgress, getNaturalViewportRect, getScrollContainerForElement, getViewportContext, clamp01 } from './viewport_progress'
import { applyEasedProgress, clearScrollAnimInlineStyles, easingFor, isUsableNaturalRect, resetElementStyleCache } from './scroll_animation_styles'
import { Utility } from '../utility/utility_controller'
import animation_lifecycle_controller from '../animations/lifecycle/animation_lifecycle_controller'
import { NAVIGATION_ANIMATION_PHASES } from '../animations/lifecycle/animation_lifecycle_events'

// Flip to true to print the dev-only per-tick perf summary to the console.
const DEBUG_LOGS = false

// Flip to true to print element-lifecycle debug logs (config parsing,
// timed-phase transitions, collect/observe summaries, plus the legacy
// `.below/.in-view` IO collector overlap warning). Useful when
// debugging "this element animated twice" or "wrong delay/duration"
// reports — each log line includes the element's relid (if present)
// and a tag so it's easy to grep. The legacy module reads the same
// flag via `window.__LAY_ANIM_DEBUG` so it can be enabled at runtime.
export const LAY_ANIM_DEBUG = false
const animDebugEnabled = () => LAY_ANIM_DEBUG || (typeof window !== 'undefined' && window.__LAY_ANIM_DEBUG === true)
// Mirror the compile-time flag onto `window` so the sibling
// `element_animate_in_on_scroll` module (which can't import from us
// without creating a circular dep) can read it without its own copy.
if (typeof window !== 'undefined') {
	window.__LAY_ANIM_DEBUG = window.__LAY_ANIM_DEBUG === true || LAY_ANIM_DEBUG
}
const debugId = (el) => {
	if (!el) return ''
	const relid = el.getAttribute && el.getAttribute('data-relid')
	if (relid) return 'relid=' + relid
	const id = el.id
	if (id) return '#' + id
	return (el.tagName || '?').toLowerCase()
}
const normalizeReEntryDirection = (value) => {
	return value === 'from_above' || value === 'both' || value === 'from_below' ? value : 'from_below'
}
const animLog = (tag, el, data) => {
	if (!animDebugEnabled()) return
	if (data !== undefined) {
		console.log('[lay-anim]', tag, debugId(el), data)
	} else {
		console.log('[lay-anim]', tag, debugId(el))
	}
}

const ANIM_ATTR = 'data-lay-scroll-anim'
const DISABLE_ON_PHONE_ATTR = 'data-lay-scroll-anim-disable-on-phone'
// "Animate again on re-entry" — when this attribute is explicitly "0",
// the timed-phase replay-arm at the bottom of `updateElementTimed`
// is skipped and the animation stays in its `done` state forever.
// Missing attribute = default replay behaviour (the long-standing
// convention).
const REPEAT_ON_RE_ENTER_ATTR = 'data-lay-scroll-anim-repeat-on-re-enter'
const REPEAT_ON_RE_ENTER_DIRECTION_ATTR = 'data-lay-scroll-anim-repeat-on-re-enter-direction'
const SCRUB_ATTR = 'data-lay-scroll-anim-scrub'
const TRIGGER_TOP_ATTR = 'data-lay-scroll-anim-trigger-top'
const DURATION_FRAMES_ATTR = 'data-lay-scroll-anim-duration-frames'
const DELAY_FRAMES_ATTR = 'data-lay-scroll-anim-delay-frames'
const STAGGER_ATTR = 'data-lay-scroll-anim-stagger'
const SCALE_FACTOR_ATTR = 'data-lay-scroll-anim-scale-factor'
const DIRECTION_ATTR = 'data-lay-scroll-anim-direction'
const DISTANCE_ATTR = 'data-lay-scroll-anim-distance'
const SKEW_X_ATTR = 'data-lay-scroll-anim-skew-x'
const SKEW_Y_ATTR = 'data-lay-scroll-anim-skew-y'
const ROTATE_X_ATTR = 'data-lay-scroll-anim-rotate-x'
const ROTATE_Y_ATTR = 'data-lay-scroll-anim-rotate-y'
const ROTATE_Z_ATTR = 'data-lay-scroll-anim-rotate-z'
const FADE_FROM_ATTR = 'data-lay-scroll-anim-fade-from'
const BLUR_FROM_ATTR = 'data-lay-scroll-anim-blur-from'
const GRAYSCALE_FROM_ATTR = 'data-lay-scroll-anim-grayscale-from'
const BRIGHTNESS_FROM_ATTR = 'data-lay-scroll-anim-brightness-from'
const REVEAL_ATTR = 'data-lay-scroll-anim-reveal'
const END_AT_TOP_ATTR = 'data-lay-scroll-anim-end-at-top'
const END_AT_FULL_EXIT_ATTR = 'data-lay-scroll-anim-end-at-full-exit'
const EASING_ATTR = 'data-lay-scroll-anim-easing'
const BASE_TRANSFORM_ATTR = 'data-lay-scroll-anim-base-transform'
const BASE_OPACITY_ATTR = 'data-lay-scroll-anim-base-opacity'


// allCandidates / allConfigs: every `[data-lay-scroll-anim]` element on the
// page, regardless of phone-gate. elements / elementConfigs are the FILTERED
// active subset that the rAF loop actually ticks. The split lets us toggle
// the phone-gate live (on `window_width_changed`) without re-querying the
// DOM or re-parsing every config.
let allCandidates = []
let allConfigs = []
let elements = [] // active decorated elements on the current page
let elementConfigs = [] // parallel array: per-element parsed config (no per-tick getAttribute)
let rafId = null

// Group-trigger coordination for Build Animation groups.
// Key: `data-lay-build-anim-group` id. Value: timestamp (perf.now) when
// the FIRST member of the group fired its idle→pending/active transition.
// Other members of the same group force-trigger at the same time origin
// even if they're still below their own trigger line — that's the
// definition of a build-anim group: ANY member entering the viewport
// fires ALL of them, with their respective stagger delays baked in via
// `data-lay-scroll-anim-delay-frames`.
//
// Cleared per group when any member resets to idle (= re-entry reset),
// so the next time the user scrolls the group back into view, the cascade
// fires fresh from the start.
const triggeredGroups = new Map()
const getGlobalTriggeredGroups = () => {
	if (typeof window === 'undefined') return null
	if (!window._layBuildAnimTriggeredGroups) {
		window._layBuildAnimTriggeredGroups = {}
	}
	return window._layBuildAnimTriggeredGroups
}
const getBuildAnimGroupTriggered = (groupId) => {
	if (!groupId) return undefined
	const localTriggeredAt = triggeredGroups.get(groupId)
	if (localTriggeredAt != null) return localTriggeredAt
	const globalTriggeredGroups = getGlobalTriggeredGroups()
	const globalTriggeredAt = globalTriggeredGroups ? globalTriggeredGroups[groupId] : undefined
	if (globalTriggeredAt != null) {
		triggeredGroups.set(groupId, globalTriggeredAt)
	}
	return globalTriggeredAt
}
const setBuildAnimGroupTriggered = (groupId, triggeredAt) => {
	triggeredGroups.set(groupId, triggeredAt)
	const globalTriggeredGroups = getGlobalTriggeredGroups()
	if (globalTriggeredGroups) {
		globalTriggeredGroups[groupId] = triggeredAt
	}
	try {
		window.dispatchEvent(new CustomEvent('lay-build-anim-group-triggered', {
			detail: { groupId, triggeredAt },
		}))
	} catch (_) {
		// CustomEvent constructor unsupported in legacy browsers.
	}
}
const clearBuildAnimGroupTriggered = (groupId) => {
	triggeredGroups.delete(groupId)
	const globalTriggeredGroups = getGlobalTriggeredGroups()
	if (globalTriggeredGroups) {
		delete globalTriggeredGroups[groupId]
	}
}
const getAnimationNow = () => {
	if (typeof window === 'undefined') return 0
	return window.performance && typeof window.performance.now === 'function'
		? window.performance.now()
		: Date.now()
}
const isBuildAnimWaitingForNavigationOrigin = (el, cfg) => {
	if (!cfg || !cfg.buildAnimGroupId || typeof window === 'undefined') return false
	if (window._layAnimTimeOrigin) return false
	if (el && el.closest) {
		const navTransitionTarget = el.closest('.lay-nav-transition-in-target')
		if (navTransitionTarget && navTransitionTarget.classList && navTransitionTarget.classList.contains('disable-css-transitions')) {
			return true
		}
	}
	if (!animation_lifecycle_controller.getNavigationState) return false
	const navigationState = animation_lifecycle_controller.getNavigationState()
	return navigationState && navigationState.phase === NAVIGATION_ANIMATION_PHASES.IN_STARTED
}
let prefersReducedMotion = false
let resumeAfterHiddenTabQueued = false
// Vertical "near viewport" margin (in vh fractions) for our cheap natural-rect
// check that replaces IntersectionObserver. With horizontal animations the IO
// post-transform rect can be far off-screen left, which makes IO miss the
// element entirely — driving updates from the natural rect bypasses that.
const NEAR_VIEWPORT_MARGIN_VH = 0.5
let scrollIdleTimeoutId = null
const SCROLL_IDLE_TIMEOUT_MS = 600

// fullpage.js translates the section wrapper via CSS transforms instead of
// scrolling the window, so no native `scroll` event ever fires and our
// progress math never advances — elements would freeze at progress=0
// (typically `opacity: 0` / `scale(0.9)`) forever and never reveal. While
// `html.fp-enabled` is on we skip the rAF loop entirely and clear inline
// styles so each element renders at its natural rest state (= what an
// already-completed scroll animation would look like). The CSS scroll-snap
// path keeps native scroll events and works fine — only the JS / fullpage.js
// mode hits this.
const isFullpageActive = () => {
	return (
		typeof document !== 'undefined' &&
		document.documentElement &&
		document.documentElement.classList.contains('fp-enabled')
	)
}

/**
 * Parse all scroll-anim data attributes off an element ONCE, so the per-tick
 * loop doesn't re-getAttribute every frame. Attribute values are static after
 * mount (PHP-emitted, not mutated by JS), so it's safe to cache. Re-runs on
 * every `collectAndObserve` (i.e., on AJAX navs) which builds a fresh element
 * list anyway.
 */
const parseElementConfig = (el) => {
	const scaleFactorRaw = parseFloat(el.getAttribute(SCALE_FACTOR_ATTR))
	const distanceRaw = parseFloat(el.getAttribute(DISTANCE_ATTR))
	const skewXRaw = parseFloat(el.getAttribute(SKEW_X_ATTR))
	const skewYRaw = parseFloat(el.getAttribute(SKEW_Y_ATTR))
	const rotateXRaw = parseFloat(el.getAttribute(ROTATE_X_ATTR))
	const rotateYRaw = parseFloat(el.getAttribute(ROTATE_Y_ATTR))
	const rotateZRaw = parseFloat(el.getAttribute(ROTATE_Z_ATTR))
	const fadeFromRaw = parseFloat(el.getAttribute(FADE_FROM_ATTR))
	const blurFromRaw = parseFloat(el.getAttribute(BLUR_FROM_ATTR))
	const grayscaleFromRaw = parseFloat(el.getAttribute(GRAYSCALE_FROM_ATTR))
	const brightnessFromRaw = parseFloat(el.getAttribute(BRIGHTNESS_FROM_ATTR))
	const endAtTopRaw = parseFloat(el.getAttribute(END_AT_TOP_ATTR))
	const easingName = el.getAttribute(EASING_ATTR) || 'easeOutSine'

	// Non-scrub (timed) mode: animation plays once over `durationFrames`
	// after the element's top crosses `triggerTopPct%` of the viewport.
	// `data-…-scrub="0"` switches modes; absence means scrub (legacy default).
	const scrubAttr = el.getAttribute(SCRUB_ATTR)
	const scrubMode = scrubAttr !== '0'
	const triggerTopRaw = parseFloat(el.getAttribute(TRIGGER_TOP_ATTR))
	const durationFramesRaw = parseFloat(el.getAttribute(DURATION_FRAMES_ATTR))
	// Flat delay between trigger and start of the animation, in frames.
	// Lets multiple in-view elements run their non-scrub animations in a
	// deliberate sequence on page load (set increasing delays per element).
	// PHP emits the attribute only when > 0 — absence reads as 0 here.
	const delayFramesRaw = parseFloat(el.getAttribute(DELAY_FRAMES_ATTR))
	const delayFrames = isFinite(delayFramesRaw) && delayFramesRaw > 0 ? delayFramesRaw : 0
	// Stagger factor: ms per px of diagonal distance. Absent or <=0 = off.
	const staggerFactorRaw = parseFloat(el.getAttribute(STAGGER_ATTR))
	const staggerFactor = isFinite(staggerFactorRaw) && staggerFactorRaw > 0 ? staggerFactorRaw : 0

	// will-change hint: list only the CSS properties that this element will
	// actually animate. Setting `will-change: transform, opacity, filter,
	// clip-path` blanket promotes a GPU layer and reserves compositor
	// resources — for non-scrub items we set this when an animation starts
	// and clear it when the animation reaches `done`, so we don't hold layers
	// permanently. Computed once here to avoid string concat in the hot loop.
	const willChangeParts = []
	const transformAnimated = el.hasAttribute(SCALE_FACTOR_ATTR)
		|| el.hasAttribute(DIRECTION_ATTR)
		|| el.hasAttribute(SKEW_X_ATTR)
		|| el.hasAttribute(ROTATE_X_ATTR)
	if (transformAnimated) willChangeParts.push('transform')
	if (el.hasAttribute(FADE_FROM_ATTR)) willChangeParts.push('opacity')
	if (el.hasAttribute(BLUR_FROM_ATTR)
		|| el.hasAttribute(GRAYSCALE_FROM_ATTR)
		|| el.hasAttribute(BRIGHTNESS_FROM_ATTR)) {
		willChangeParts.push('filter')
	}
	if (el.hasAttribute(REVEAL_ATTR)) willChangeParts.push('clip-path')
	const willChangeStr = willChangeParts.join(', ')

	return {
		scaleEnabled: el.hasAttribute(SCALE_FACTOR_ATTR),
		directionEnabled: el.hasAttribute(DIRECTION_ATTR),
		skewEnabled: el.hasAttribute(SKEW_X_ATTR),
		rotateEnabled: el.hasAttribute(ROTATE_X_ATTR),
		fadeEnabled: el.hasAttribute(FADE_FROM_ATTR),
		blurEnabled: el.hasAttribute(BLUR_FROM_ATTR),
		grayscaleEnabled: el.hasAttribute(GRAYSCALE_FROM_ATTR),
		brightnessEnabled: el.hasAttribute(BRIGHTNESS_FROM_ATTR),
		revealEnabled: el.hasAttribute(REVEAL_ATTR),
		willChangeStr: willChangeStr,
		baseTransform: el.getAttribute(BASE_TRANSFORM_ATTR) || '',
		baseOpacity: el.getAttribute(BASE_OPACITY_ATTR) || '',
		direction: el.getAttribute(DIRECTION_ATTR) || 'left',
		revealDirection: el.getAttribute(REVEAL_ATTR) || 'left',
		endAtTop: isFinite(endAtTopRaw) ? endAtTopRaw : 90,
		endAtFullExit: el.hasAttribute(END_AT_FULL_EXIT_ATTR),
		// Pre-pick easing fn so we don't do a string lookup per tick.
		easingFn: easingFor(easingName),
		scaleFactor: isFinite(scaleFactorRaw) ? scaleFactorRaw : 0.85,
		distancePct: isFinite(distanceRaw) ? distanceRaw / 100 : 1,
		skewFromX: isFinite(skewXRaw) ? skewXRaw : 0,
		skewFromY: isFinite(skewYRaw) ? skewYRaw : 0,
		rotateFromX: isFinite(rotateXRaw) ? rotateXRaw : 0,
		rotateFromY: isFinite(rotateYRaw) ? rotateYRaw : 0,
		rotateFromZ: isFinite(rotateZRaw) ? rotateZRaw : 0,
		fadeFrom: isFinite(fadeFromRaw) ? fadeFromRaw : 0,
		blurFromPx: isFinite(blurFromRaw) ? blurFromRaw : 10,
		grayscaleFromPct: isFinite(grayscaleFromRaw) ? grayscaleFromRaw : 100,
		brightnessFromPct: isFinite(brightnessFromRaw) ? brightnessFromRaw : 30,
		// Phone-gate flag — `applyPhoneGate` reads this to decide whether
		// the element participates in the rAF loop on the current screen size.
		disableOnPhone: el.getAttribute(DISABLE_ON_PHONE_ATTR) === '1',
		// Default true (replay) — only false when explicitly opted out.
		repeatOnReEnter: el.getAttribute(REPEAT_ON_RE_ENTER_ATTR) !== '0',
		repeatOnReEnterDirection: normalizeReEntryDirection(el.getAttribute(REPEAT_ON_RE_ENTER_DIRECTION_ATTR)),
		// "Scale Animations based on element size" — when set by the build-anim
		// group resolver, the translate distance is capped at the element's
		// own width/height so small thumbnails don't fly in from miles away.
		scaleToElementSize: el.getAttribute('data-lay-scroll-anim-scale-to-elsize') === '1',
		// Non-scrub (timed) mode bookkeeping. _timedPhase: 'idle' (waiting
		// for trigger), 'pending' (stagger delay after trigger), 'active'
		// (interpolating over duration), 'done' (frozen at progress=1).
		// _staggerDelayMs is computed on idle→pending transition from the
		// element's diagonal distance to viewport top-left at that moment,
		// matching the legacy element_animate_in_on_scroll wave effect.
		scrubMode: scrubMode,
		triggerTopPct: isFinite(triggerTopRaw) ? triggerTopRaw : 90,
		durationFrames: isFinite(durationFramesRaw) ? Math.max(0, durationFramesRaw) : 60,
		delayFrames: delayFrames,
		staggerFactor: staggerFactor,
		_timedPhase: scrubMode ? null : 'idle',
		_timedStartMs: 0,
		_staggerDelayMs: 0,
		// Cached `data-lay-build-anim-group` id — used by the rAF tick's
		// near-viewport gate to LET group members through even when
		// they're far below the fold, so a group fires all members
		// together when ANY of them enters the viewport.
		buildAnimGroupId: (el.getAttribute && el.getAttribute('data-lay-build-anim-group')) || null,
		_dbgEl: el,
	}
}

// Helper used by both parseElementConfig (init log) and the timed-phase
// transitions below — single source for "what's this element doing".
const animLogPhase = (cfg, from, to, extra) => {
	if (!animDebugEnabled()) return
	const data = Object.assign(
		{
			from: from,
			to: to,
			durationFrames: cfg.durationFrames,
			delayFrames: cfg.delayFrames,
			staggerFactor: cfg.staggerFactor,
			scrubMode: cfg.scrubMode,
			easing: cfg.easingFn && cfg.easingFn.name,
		},
		extra || {},
	)
	console.log('[lay-anim] phase', debugId(cfg._dbgEl), data)
}

const getResetNaturalRect = (el, cfg) => {
	const rect = getNaturalViewportRect(el)
	if (isUsableNaturalRect(rect)) return rect
	return cfg._lastNaturalRect || rect
}

const resetElementToStart = (el, cfg) => {
	if (!el || !cfg) return
	el.removeAttribute('data-lay-scroll-anim-trigger-play-complete')
	if (cfg.scrubMode) {
		cfg._fpScrubPhase = 'idle'
		cfg._fpScrubStartMs = 0
	} else {
		cfg._timedPhase = 'idle'
		cfg._timedStartMs = 0
		cfg._staggerDelayMs = 0
	}
	resetElementStyleCache(cfg)
	if (el.style.willChange) el.style.willChange = ''
	applyEasedProgress(el, cfg, getResetNaturalRect(el, cfg), cfg.easingFn(0))
}

const updateElement = (el, cfg, naturalRect, ctx) => {
	const linear = getProgress(naturalRect, cfg.endAtTop, cfg.endAtFullExit, 0, ctx)
	const eased = cfg.easingFn(linear)
	applyEasedProgress(el, cfg, naturalRect, eased)
}

// Non-scrub timed playback. Mirrors the trigger semantic of textanimations
// (rect.top crosses `triggerTopPct%` of viewport height) but applies the same
// effect engine (scale / fade / blur / reveal / …) as the scrub path. Once
// progress reaches 1 the element freezes — no rewind on scroll-up.
//
// Returns true if the element still needs ticking (pending/active phase),
// false once it's done. The tick loop uses this to keep the rAF alive while
// timed animations are running, even when the page isn't being scrolled.
const FRAME_MS = 1000 / 60

// Element is in `display: none` (or detached) — `offsetParent` is null in
// that case. Hidden items must NOT advance their timed phase: their
// `naturalRect` collapses to {0,0,0,0} which would otherwise cross the
// trigger line and run the animation invisibly. When the thumbnail-grid
// filter unhides them later, the `thumbnailGridWasFiltered` listener
// resets the phase so they animate fresh.
const isHidden = (el) => el.offsetParent === null && el.offsetWidth === 0

const updateElementTimed = (el, cfg, naturalRect, ctx, nowMs) => {
	if (isHidden(el)) {
		if (isFullpageActive() && !cfg._fpHiddenLogged) {
			cfg._fpHiddenLogged = true
			console.log('[fp-anim] updateElementTimed: bail isHidden', { offsetParent: el.offsetParent, offsetWidth: el.offsetWidth, classes: (el.className || '').toString().slice(0, 60) })
		}
		return false
	}
	if (cfg._timedPhase === 'done') {
		// Build-anim group members re-arm as ONE unit — see the group-level
		// reset after the tick loop. An individual member must never
		// self-reset to idle here: that is exactly what broke the cascade
		// (each member re-triggering on its own as it scrolled back into
		// view, instead of the whole group replaying together).
		if (cfg.buildAnimGroupId) return false
		// Replay support — mirrors the legacy `element_animate_in_on_scroll`
		// IO module: when the element scrolls fully past the bottom of the
		// viewport (rect.top > vh), reset to 'idle' so re-entering from
		// below triggers a fresh playback. Scrolling past ABOVE the
		// viewport does NOT reset (animation stays played, no replay on
		// scroll-back-down). The next tick from idle paints progress=0,
		// so the element is in its pre-animation state when it peeks back
		// into the viewport.
		// Under fullpage.js this is a trap: elements in non-active sections
		// always satisfy `rect.top > vh` (offsetTop-based natural rect
		// doesn't follow fp's transform), so a done element would reset to
		// idle every tick and the next tick would paint progress=0 — visible
		// content disappears the moment its animation finishes. The
		// `fpAfterLoad` handler explicitly re-arms phase=active when the
		// user navigates back to a section, so we don't need the auto-reset.
		// "Animate again on re-entry" off → freeze in `done` forever,
		// regardless of how far the element has scrolled past the
		// viewport. The arm-replay step below is the only way out of
		// `done`; skipping it makes the animation a clean one-shot.
		const reEntryMode = cfg.repeatOnReEnterDirection || 'from_below'
		const allowBelow = reEntryMode === 'from_below' || reEntryMode === 'both'
		const allowAbove = reEntryMode === 'from_above' || reEntryMode === 'both'
		const isBelow = naturalRect.top > ctx.vh
		const isAbove = naturalRect.bottom < 0
		if (cfg.repeatOnReEnter !== false && !isFullpageActive() && ((allowBelow && isBelow) || (allowAbove && isAbove))) {
			el.removeAttribute('data-lay-scroll-anim-trigger-play-complete')
			animLogPhase(cfg, 'done', 'idle', { reason: isAbove ? 'scrolled-above-viewport (replay arm)' : 'scrolled-below-viewport (replay arm)' })
			cfg._timedPhase = 'idle'
			cfg._timedStartMs = 0
			// Arm a "waiting for re-entry" flag so the idle path knows
			// not to re-trigger on the very next tick. Without this, an
			// element parked permanently out-of-view (e.g. user scrolled
			// past the group and stopped) would: done → idle → trigger
			// immediately (idle's `rect.top > triggerY` is false for an
			// element above the viewport) → active → done → repeat,
			// infinitely. The flag is cleared when the element actually
			// scrolls BACK into view in `updateElementTimed`.
			cfg._waitingForReEntry = isAbove ? 'above' : 'below'
			// If this element is part of a build-anim group, clear the
			// group's triggered timestamp so the next re-entry restarts
			// the whole cascade together (all members fire at the same
			// time origin again, with their staggered delays).
			if (cfg.buildAnimGroupId) clearBuildAnimGroupTriggered(cfg.buildAnimGroupId)
		}
		return false
	}

	// will-change is set when the animation enters its active window
	// (idle → pending or idle → active) and cleared when it reaches done.
	// Promotes the element to a GPU compositor layer for the duration of
	// the animation. Permanent will-change is a known anti-pattern (memory
	// bloat, especially on mobile with many items) — toggling per-animation
	// gives us the GPU benefit without holding the layer afterwards.
	const setWillChange = () => {
		if (cfg.willChangeStr && el.style.willChange !== cfg.willChangeStr) {
			el.style.willChange = cfg.willChangeStr
		}
	}
	const clearWillChange = () => {
		if (el.style.willChange) el.style.willChange = ''
	}

	if (cfg._timedPhase === 'idle') {
		el.removeAttribute('data-lay-scroll-anim-trigger-play-complete')
		// Trigger condition: element top crossed triggerTopPct% of viewport.
		// triggerTopPct=80 → trigger at 80% from viewport top (= 20% above
		// bottom). Higher value = triggers later (closer to viewport bottom).
		const triggerY = ctx.vh * (cfg.triggerTopPct / 100)
		// Build-anim group fast-path: if THIS element is part of a group
		// whose other member already fired, force-trigger this one with
		// the same time origin. Each member keeps its own `delayFrames`
		// (resolved at build time by the group's stagger + order), so
		// the cascade still plays as authored — but it starts as soon as
		// ANY member enters the viewport, not when each individually
		// crosses its trigger line.
		const _groupId = cfg.buildAnimGroupId
		const groupTriggeredAt = _groupId ? getBuildAnimGroupTriggered(_groupId) : undefined
		// Re-entry gate: after a `done → idle` reset the element is still
		// parked out-of-view. The next trigger should fire only AFTER the
		// user has actually scrolled it back into the viewport — without
		// this an off-screen element loops (done → idle → re-trigger →
		// active → done → reset → repeat).
		// EXCEPTION: a group member whose group has ALREADY been triggered
		// by a sibling skips the gate, so it joins the cascade at the
		// shared time origin even while still parked off-screen.
		if (cfg._waitingForReEntry && groupTriggeredAt == null) {
			const isInView = naturalRect.top < ctx.vh && naturalRect.bottom > 0
			if (!isInView) {
				applyEasedProgress(el, cfg, naturalRect, cfg.easingFn(0))
				return false
			}
		}
		cfg._waitingForReEntry = null
		if (groupTriggeredAt == null && naturalRect.top > triggerY) {
			// Below trigger line, no group-trigger to follow — keep pre-animation state.
			applyEasedProgress(el, cfg, naturalRect, cfg.easingFn(0))
			return false
		}
		if (isBuildAnimWaitingForNavigationOrigin(el, cfg)) {
			applyEasedProgress(el, cfg, naturalRect, cfg.easingFn(0))
			return true
		}
		// Sync-with-Nav-Anim: hold the pre-state until the nav-anim
		// timeline begins. The sync controller flips `_laySyncFired` on
		// `transition_navigation_in_started`, which is the same time
		// origin as the col cascade.
		if (el.hasAttribute && el.hasAttribute('data-lay-sync-nav-anim') && el._laySyncFired !== true) {
			applyEasedProgress(el, cfg, naturalRect, cfg.easingFn(0))
			return true
		}
		// Stagger: per-element delay based on the item's ROW-LOCAL index ×
		// staggerFactor frames. Each row of the grid restarts the wave at
		// 0 — the leftmost item in any row gets 0 delay, the next one to
		// its right gets `staggerFactor` frames more, etc.
		//
		// "Same row" = vertical ranges overlap (handles ragged masonry
		// where row tops aren't perfectly aligned because of varied item
		// heights). "Before" = to the left in the viewport.
		//
		// Why row-local (not global DOM index):
		//   With global indexing, the first item in row 2 inherits all the
		//   delay budget from row 1 (e.g. 4 items per row × factor 5 →
		//   row 2's first item waits 4×5×16ms = 320ms before starting).
		//   That contradicts the user's expectation that "the first item
		//   in each wave starts immediately when it enters the viewport".
		//
		// We measure positions via the parent wrap's bounding rect (which
		// has the masonry transform but not our scroll-anim transform —
		// see updateElementTimed for full reasoning).
		let staggerComputedMs = 0
		if (cfg.staggerFactor > 0 && el.parentElement && el.parentElement.parentElement) {
			const parent = el.parentElement
			const grid = parent.parentElement
			const parentRect = parent.getBoundingClientRect()
			let rowIndex = 0
			for (let i = 0; i < grid.children.length; i++) {
				const sib = grid.children[i]
				if (sib === parent) continue
				const sibRect = sib.getBoundingClientRect()
				// Same row if vertical ranges overlap.
				const sameRow = sibRect.top < parentRect.bottom && sibRect.bottom > parentRect.top
				if (sameRow && sibRect.left < parentRect.left) {
					rowIndex++
				}
			}
			staggerComputedMs = rowIndex * cfg.staggerFactor * FRAME_MS
		}
		// Flat `delayFrames` is added on top of any stagger-computed delay,
		// so authors can both sequence elements with `delay` AND keep their
		// in-grid stagger cascade. For single elements (no stagger), the
		// flat delay is the only contributor.
		const flatDelayMs = cfg.delayFrames * FRAME_MS
		cfg._staggerDelayMs = flatDelayMs + staggerComputedMs
		const nextPhase = cfg._staggerDelayMs > 0 ? 'pending' : 'active'
		animLogPhase(cfg, 'idle', nextPhase, { staggerDelayMs: cfg._staggerDelayMs, flatDelayMs: flatDelayMs, staggerComputedMs: staggerComputedMs })
		if (animDebugEnabled() && el.hasAttribute && el.hasAttribute('data-lay-sync-nav-anim')) {
			console.log('[lay-sync] scroll triggered', {
				t: performance.now().toFixed(1),
				delayMs: Math.round(cfg._staggerDelayMs),
				durationFrames: cfg.durationFrames,
				easing: el.getAttribute && el.getAttribute('data-lay-scroll-anim-easing'),
			})
		}
		cfg._timedPhase = nextPhase
		// Time origin: use the group's trigger time if we're firing because
		// of a sibling, so all members share the same `_timedStartMs` and
		// their `delayFrames` cascade plays in lockstep. Otherwise use the
		// current tick time AND register this group's trigger so later
		// siblings fall into the fast-path on the next tick.
		cfg._timedStartMs = groupTriggeredAt != null ? groupTriggeredAt : nowMs
		if (_groupId && groupTriggeredAt == null) {
			setBuildAnimGroupTriggered(_groupId, nowMs)
		}
		setWillChange()
	}

	if (cfg._timedPhase === 'pending') {
		// Shared time origin with the nav-anim cascade + text animations:
		// `window._layAnimTimeOrigin` is set when myTimeline.begin fires
		// in the nav-anim controller. For elements that triggered
		// idle→pending BEFORE nav-anim begin (rAF starts ticking before
		// the content_ready chain reaches transition_in), their literal
		// `_timedStartMs` would make the `delayMs` countdown start
		// hundreds of ms earlier than text animations on the same page
		// (text waits for the IO observer, attached post-content_ready).
		// Use the later of the two so a group's `delayMs=2000` aligns
		// with a 2000ms-long top text. For elements that come into view
		// via scrolling AFTER nav-anim ends, `_timedStartMs` is later
		// than the origin, so `Math.max` keeps normal scroll-trigger
		// semantics.
		const effectiveStartMs = Math.max(cfg._timedStartMs, window._layAnimTimeOrigin || 0)
		const elapsed = nowMs - effectiveStartMs
		if (elapsed < cfg._staggerDelayMs) {
			applyEasedProgress(el, cfg, naturalRect, cfg.easingFn(0))
			return true
		}
		animLogPhase(cfg, 'pending', 'active', { waitedMs: elapsed })
		cfg._timedPhase = 'active'
		cfg._timedStartMs = nowMs
		if (typeof window.layDevModeEmit === 'function') {
			window.layDevModeEmit('scroll_anim:active', el)
		}
	}

	// active
	if (cfg.buildAnimGroupId && window._layAnimTimeOrigin && cfg._timedStartMs < window._layAnimTimeOrigin) {
		cfg._timedStartMs = window._layAnimTimeOrigin
	}
	const elapsed = nowMs - cfg._timedStartMs
	const durMs = cfg.durationFrames * FRAME_MS
	let linear
	if (durMs <= 0) {
		linear = 1
	} else {
		linear = clamp01(elapsed / durMs)
	}
	const eased = cfg.easingFn(linear)
	applyEasedProgress(el, cfg, naturalRect, eased)
	if (linear >= 1) {
		animLogPhase(cfg, 'active', 'done', { elapsedMs: elapsed })
		cfg._timedPhase = 'done'
		clearWillChange()
		el.setAttribute('data-lay-scroll-anim-trigger-play-complete', '1')
		// Notify any subscribers (drag physics today, potentially
		// others tomorrow) that this trigger&play animation has
		// finished a cycle. Drag physics waits for this signal
		// before binding its physics body so the simulation starts
		// from the animation's end-state (and doesn't fight the
		// transform during the run). On `repeat-on-re-enter`, this
		// fires again each time a new cycle completes; one-shot
		// listeners on the subscriber side handle that.
		try {
			el.dispatchEvent(new CustomEvent('lay-scroll-anim-trigger-play-complete', { bubbles: false }))
		} catch (_) {
			// CustomEvent constructor unsupported in older browsers
			// where this codebase is no longer expected to run.
		}
		return false
	}
	return true
}

// ----- visibility check + rAF loop ----------------------------------------

// Cheap "is element near the viewport" using the NATURAL rect (not the
// transformed one). With horizontal animations, the post-transform rect is
// often far off-screen left and IntersectionObserver can't see it — using
// the natural rect bypasses that whole class of bug.
const isRectNearViewport = (rect, vh) => {
	const margin = vh * NEAR_VIEWPORT_MARGIN_VH
	return rect.top < vh + margin && rect.bottom > -margin
}

// Group-level re-arm. A build-anim group plays AND replays as one unit:
// once every member has scrolled clear of the viewport, the whole group
// resets to idle so the next time any member re-enters, the entire
// cascade replays from a shared time origin. `direction` is which side
// the group went off ('above' = scrolled down past it, 'below' = up).
// Replay settings are a group-level authoring choice — read off the
// first member; `repeatOnReEnter === false` means play-once.
const resetBuildAnimGroup = (groupId, direction) => {
	let firstCfg = null
	for (let i = 0; i < elementConfigs.length; i++) {
		const c = elementConfigs[i]
		if (c && c.buildAnimGroupId === groupId) {
			firstCfg = c
			break
		}
	}
	if (!firstCfg || firstCfg.repeatOnReEnter === false) return
	const mode = firstCfg.repeatOnReEnterDirection || 'from_below'
	const allowed =
		direction === 'above' ? mode === 'from_above' || mode === 'both' : mode === 'from_below' || mode === 'both'
	if (!allowed) return
	for (let i = 0; i < elements.length; i++) {
		const c = elementConfigs[i]
		if (!c || c.buildAnimGroupId !== groupId) continue
		c._timedPhase = 'idle'
		c._timedStartMs = 0
		c._waitingForReEntry = direction
		const el = elements[i]
		if (el && el.removeAttribute) el.removeAttribute('data-lay-scroll-anim-trigger-play-complete')
	}
	clearBuildAnimGroupTriggered(groupId)
}

const tick = () => {
	if (!animation_lifecycle_controller.isDocumentVisible()) {
		rafId = null
		if (!resumeAfterHiddenTabQueued) {
			resumeAfterHiddenTabQueued = true
			animation_lifecycle_controller.runWhenDocumentVisible(() => {
				resumeAfterHiddenTabQueued = false
				onScrollOrResize()
			})
		}
		return
	}

	if (animation_lifecycle_controller.isNavigationAnimating()) {
		rafId = requestAnimationFrame(tick)
		return
	}

                     
	const _perfTickStart = performance.now()
           

	// Take ONE viewport snapshot per tick (vh, scrollTop, scrollHeight) so the
	// per-element progress calc doesn't re-read `documentElement.scrollHeight`
	// (layout-trigger) once per element. For 50 decorated elements that's a
	// 50× saving on the layout-flushing reads.
	const nowMs = performance.now()
	let anyNear = false
	let anyTimedAnimating = false
	// Under fullpage.js the natural rect (offsetTop-based) doesn't reflect
	// fp's transform-driven "scroll" — every section past the first reports
	// `top > vh`, so the near-viewport gate would skip them and the
	// fp-triggered timed animations would never tick. Bypass the gate while
	// fp is active.
	const fpActive = isFullpageActive()
	let _fpDiagOnce = false
	// Per-tick viewport coverage of each triggered build-anim group, used
	// by the group-level re-arm after the loop. Keyed by group id →
	// { total, near, above, below } member counts.
	const groupViewportState = new Map()
	for (let i = 0; i < elements.length; i++) {
		const el = elements[i]
		if (!el || !el.isConnected) continue
		const cfg = elementConfigs[i]
		// Compute the natural rect ONCE per element per tick and reuse it for
		// both the near-viewport gate and the progress calc inside
		// `updateElement`. Was previously done twice per tick per element.
		const rect = getNaturalViewportRect(el)
		const ctx = getViewportContext(getScrollContainerForElement(el))
		if (cfg._deferUntilOverlayShownEnded && el.closest('.lay-overlay')) {
			applyEasedProgress(el, cfg, rect, cfg.easingFn(0))
			continue
		}
		// Build-anim group members get a free pass through the near-
		// viewport gate once ANY sibling has triggered the group — they
		// need the tick to run their idle→pending/active transition even
		// when they're parked far below the fold (the whole point of a
		// group is that all members fire together; the leader being in-
		// view and the trailing members not yet shouldn't matter).
		const groupTriggered = cfg.buildAnimGroupId ? getBuildAnimGroupTriggered(cfg.buildAnimGroupId) != null : false
		const near = fpActive || groupTriggered || isRectNearViewport(rect, ctx.vh)
		// Tally where this build-anim group's members sit relative to the
		// viewport — the group-level re-arm below fires only once EVERY
		// member has cleared it. Tallied for ALL group members (not just
		// already-triggered ones) and BEFORE the `!near` continue, so the
		// member that just triggered the group this tick — and far-off
		// members that would `continue` — both still count; otherwise a
		// freshly triggered group could read as fully off-screen and
		// reset itself on the very tick it triggered.
		if (cfg.buildAnimGroupId) {
			let gv = groupViewportState.get(cfg.buildAnimGroupId)
			if (!gv) {
				gv = { total: 0, inView: 0, above: 0, below: 0 }
				groupViewportState.set(cfg.buildAnimGroupId, gv)
			}
			gv.total++
			// Actual-viewport test — NOT the near-viewport margin. The
			// group re-arms once every member has left the real viewport
			// (mirrors the per-element replay-arm's `rect.top > vh` /
			// `rect.bottom < 0`). Using the near-margin meant a group too
			// close to a page edge could never scroll fully past it, so
			// it never re-armed — "the group animation never restarts".
			if (rect.top < ctx.vh && rect.bottom > 0) gv.inView++
			else if (rect.bottom <= 0) gv.above++
			else gv.below++
		}
		if (fpActive && !_fpDiagOnce) {
			_fpDiagOnce = true
			console.log('[fp-anim] tick (fpActive)', { totalElements: elements.length, vh: ctx.vh, firstElPhase: cfg && cfg._timedPhase, firstElScrub: cfg && cfg.scrubMode, rectTop: rect.top })
		}
		if (!near) continue
		anyNear = true
                      
		const _perfElStart = performance.now()
            
		if (cfg.scrubMode) {
			// fp-mode override: when `fpPlayElementsIn` armed an `_fpScrubPhase`,
			// run a one-shot tween instead of the scroll-driven scrub math
			// (which would just stick at progress=0 since fp doesn't scroll).
			if (cfg._fpScrubPhase === 'active') {
				const elapsed = nowMs - cfg._fpScrubStartMs
				const linear = clamp01(elapsed / cfg._fpScrubDurationMs)
				const eased = cfg.easingFn(linear)
				applyEasedProgress(el, cfg, rect, eased)
				if (linear >= 1) {
					cfg._fpScrubPhase = 'done'
					if (el.style.willChange) el.style.willChange = ''
				} else {
					anyTimedAnimating = true
				}
			} else {
				updateElement(el, cfg, rect, ctx)
			}
		} else {
			// Non-scrub timed playback — keep loop alive while pending/active.
			const stillAnimating = updateElementTimed(el, cfg, rect, ctx, nowMs)
			if (stillAnimating) anyTimedAnimating = true
		}
                      
		recordElementPerf(el, cfg, performance.now() - _perfElStart)
            
	}

	// Group-level re-arm: once EVERY member of a triggered build-anim
	// group has scrolled clear of the viewport, reset the whole group so
	// the next re-entry replays the cascade from a shared time origin.
	// Skipped under fullpage.js — its transform-driven scroll makes the
	// natural-rect off-screen test meaningless (same reason the
	// per-element replay-arm bails on `isFullpageActive()`).
	if (!fpActive && triggeredGroups.size > 0) {
		triggeredGroups.forEach((triggeredAt, groupId) => {
			const gv = groupViewportState.get(groupId)
			if (!gv || gv.inView > 0) return
			if (gv.above === gv.total) resetBuildAnimGroup(groupId, 'above')
			else if (gv.below === gv.total) resetBuildAnimGroup(groupId, 'below')
		})
	}

                     
	flushPerfIfDue(performance.now() - _perfTickStart, anyNear)
           

	// Keep ticking while scroll is active, or while any element is near
	// the viewport (covers passive content shifts, e.g. resize, image-load
	// reflow), or while a non-scrub timed animation is still running.
	if ((anyNear && scrollIdleTimeoutId !== null) || anyTimedAnimating) {
		rafId = requestAnimationFrame(tick)
	} else {
		rafId = null
	}
}

                    
// Per-tick perf collector. Aggregates 1 sec worth of ticks then logs the top
// offenders so we can see which animation type/element combination is
// dragging the frame budget. Stripped from prod via ifdef.
const _perfBucket = {
	tickCount: 0,
	totalTickMs: 0,
	maxTickMs: 0,
	elements: new Map(), // el → { totalMs, maxMs, count, label }
	flushAtMs: 0,
}
const FEATURE_TAGS = [
	['directionEnabled', 'direction'],
	['scaleEnabled', 'scale'],
	['skewEnabled', 'skew'],
	['rotateEnabled', 'rotate'],
	['fadeEnabled', 'fade'],
	['blurEnabled', 'blur'],
	['grayscaleEnabled', 'grayscale'],
	['brightnessEnabled', 'brightness'],
	['revealEnabled', 'reveal'],
]
function describeElementForPerf(el, cfg) {
	const features = []
	for (let i = 0; i < FEATURE_TAGS.length; i++) {
		if (cfg[FEATURE_TAGS[i][0]]) features.push(FEATURE_TAGS[i][1])
	}
	const tag = (el.tagName || '?').toLowerCase()
	const idPart = el.id ? '#' + el.id : ''
	const classes = (el.className || '').toString().split(/\s+/).filter(Boolean).slice(0, 2)
	const classPart = classes.length ? '.' + classes.join('.') : ''
	return tag + idPart + classPart + '[' + features.join(',') + ']'
}
function recordElementPerf(el, cfg, ms) {
	let entry = _perfBucket.elements.get(el)
	if (!entry) {
		entry = { totalMs: 0, maxMs: 0, count: 0, label: describeElementForPerf(el, cfg) }
		_perfBucket.elements.set(el, entry)
	}
	entry.totalMs += ms
	if (ms > entry.maxMs) entry.maxMs = ms
	entry.count++
}
function flushPerfIfDue(tickMs, anyNear) {
	if (anyNear) {
		_perfBucket.tickCount++
		_perfBucket.totalTickMs += tickMs
		if (tickMs > _perfBucket.maxTickMs) _perfBucket.maxTickMs = tickMs
	}
	const now = performance.now()
	if (_perfBucket.flushAtMs === 0) _perfBucket.flushAtMs = now + 1000
	if (now < _perfBucket.flushAtMs || _perfBucket.tickCount === 0) return

	const avgTick = _perfBucket.totalTickMs / _perfBucket.tickCount
	const ranked = []
	_perfBucket.elements.forEach((entry) => {
		ranked.push({
			label: entry.label,
			avgMs: entry.totalMs / entry.count,
			maxMs: entry.maxMs,
			count: entry.count,
		})
	})
	ranked.sort((a, b) => b.avgMs - a.avgMs)
	const top = ranked.slice(0, 5)
	if (DEBUG_LOGS) {
		console.log(
			'[scroll-anim perf] ticks=' +
				_perfBucket.tickCount +
				' avgTick=' +
				avgTick.toFixed(2) +
				'ms maxTick=' +
				_perfBucket.maxTickMs.toFixed(2) +
				'ms — top:',
		)
		for (let i = 0; i < top.length; i++) {
			const r = top[i]
			console.log(
				'  ' +
					(i + 1) +
					'. avg=' +
					r.avgMs.toFixed(2) +
					'ms max=' +
					r.maxMs.toFixed(2) +
					'ms (' +
					r.count +
					'×) — ' +
					r.label,
			)
		}
	}
	_perfBucket.tickCount = 0
	_perfBucket.totalTickMs = 0
	_perfBucket.maxTickMs = 0
	_perfBucket.elements = new Map()
	_perfBucket.flushAtMs = now + 1000
}
          

const ensureLoopRunning = () => {
	if (rafId == null) {
		rafId = requestAnimationFrame(tick)
	}
}

const onScrollOrResize = () => {
	// Mark scroll as active and schedule a "scroll idle" reset.
	if (scrollIdleTimeoutId !== null) {
		clearTimeout(scrollIdleTimeoutId)
	}
	scrollIdleTimeoutId = window.setTimeout(() => {
		scrollIdleTimeoutId = null
	}, SCROLL_IDLE_TIMEOUT_MS)
	ensureLoopRunning()
}

let listenersAttached = false
const attachScrollListeners = () => {
	if (listenersAttached) return
	listenersAttached = true
	// Capture-phase so scrolls inside sub-scrollers (notably `#lay-project-overlay`,
	// which has its own internal scroll and does NOT scroll the window) still
	// drive the rAF loop. `scroll` events don't bubble but they do dispatch
	// through capture.
	window.addEventListener('scroll', onScrollOrResize, { passive: true, capture: true })
	// `window_width_changed` (custom event from utility_controller) instead of
	// the native `resize`: on iOS, mobile Safari fires `resize` every time the
	// browser chrome shows/hides during scroll, which is just visual jitter,
	// not a real layout change. The custom event filters those out and only
	// fires when innerWidth actually changes.
	jQuery(window).on('window_width_changed', () => {
		// Re-apply the phone-gate so elements with `disable-on-phone` either
		// drop out of (or re-enter) the active set live as the user resizes
		// across the phone breakpoint.
		applyPhoneGate()
		onScrollOrResize()
	})
}

// Filter `allCandidates` into `elements` / `elementConfigs` based on the
// current screen size + each element's disable-on-phone flag. Elements that
// transition from active → disabled get their PHP-painted initial inline
// styles cleared so they snap to natural rest state. Elements that
// transition from disabled → active get an init pass so they appear in
// their progress=0 state instead of whatever was last painted.
//
// Cheap to run repeatedly — no DOM query, no config re-parse. Called from
// `collectAndObserve` (initial collect, page nav) and from the
// `window_width_changed` handler (live phone-gate toggle on resize).
const applyPhoneGate = () => {
	const isPhone = !!(Utility && Utility.isPhoneSize)
	const wasActive = new Set(elements)
	const nextActive = []
	const nextConfigs = []
	const newlyDisabled = []
	for (let i = 0; i < allCandidates.length; i++) {
		const el = allCandidates[i]
		const cfg = allConfigs[i]
		if (isPhone && cfg.disableOnPhone) {
			if (wasActive.has(el)) newlyDisabled.push({ el, cfg })
		} else {
			nextActive.push(el)
			nextConfigs.push(cfg)
		}
	}
	newlyDisabled.forEach(({ el, cfg }) => {
		el.style.transform = ''
		el.style.opacity = cfg.baseOpacity || ''
		el.style.filter = ''
		el.style.clipPath = ''
		if (el.style.willChange) el.style.willChange = ''
	})
	const newlyActive = []
	for (let i = 0; i < nextActive.length; i++) {
		if (!wasActive.has(nextActive[i])) newlyActive.push({ el: nextActive[i], cfg: nextConfigs[i] })
	}
	elements = nextActive
	elementConfigs = nextConfigs
	// Init pass for newly-active elements only (avoid resetting
	// already-running animations on the existing-active set).
	if (!prefersReducedMotion && newlyActive.length > 0 && animation_lifecycle_controller.isDocumentVisible()) {
		const nowMs = performance.now()
		for (let i = 0; i < newlyActive.length; i++) {
			const { el, cfg } = newlyActive[i]
			const rect = getNaturalViewportRect(el)
			const ctx = getViewportContext(getScrollContainerForElement(el))
			if (cfg.scrubMode) {
				updateElement(el, cfg, rect, ctx)
			} else {
				updateElementTimed(el, cfg, rect, ctx, nowMs)
			}
		}
	}
}

const collectAndObserve = () => {
	if (rafId != null) {
		cancelAnimationFrame(rafId)
		rafId = null
	}
	if (scrollIdleTimeoutId !== null) {
		clearTimeout(scrollIdleTimeoutId)
		scrollIdleTimeoutId = null
	}

	// `newpageshown` and other Lay events can fire `collectAndObserve`
	// multiple times in quick succession during a single page load.
	// Reusing the previous config object for elements that survive a
	// re-collect preserves `_timedPhase`, so an animation that's already
	// `active` or `done` doesn't get reset to `idle` and replayed —
	// previously this was the visible "element animates twice" bug.
	const prevByEl = new WeakMap()
	for (let i = 0; i < allCandidates.length; i++) {
		if (allCandidates[i] && allConfigs[i]) prevByEl.set(allCandidates[i], allConfigs[i])
	}

	allCandidates = Array.from(document.querySelectorAll('[' + ANIM_ATTR + ']')).filter((el) => {
		return !(el.closest && el.closest('.lay-row-fixed') && el.getAttribute(SCRUB_ATTR) !== '0')
	})
	allConfigs = allCandidates.map((el) => {
		const prev = prevByEl.get(el)
		return prev || parseElementConfig(el)
	})
	if (animDebugEnabled()) {
		console.log('[lay-anim] collectAndObserve picked', allCandidates.length, 'element(s)')
		allCandidates.forEach((el, i) => {
			const cfg = allConfigs[i]
			console.log('[lay-anim]   →', debugId(el), {
				scrub: cfg.scrubMode,
				durationFrames: cfg.durationFrames,
				delayFrames: cfg.delayFrames,
				staggerFactor: cfg.staggerFactor,
				easing: el.getAttribute(EASING_ATTR),
				fade: cfg.fadeEnabled,
				direction: cfg.directionEnabled ? cfg.direction : null,
				disableOnPhone: cfg.disableOnPhone,
			})
		})
	}
	// Idempotency — do NOT reset `elements` / `elementConfigs` here.
	// Resetting them made every candidate look "newly active" in
	// `applyPhoneGate`'s `wasActive` comparison, so every re-collect
	// (newpageshown, project_overlay_open_end, content_ready, etc.)
	// re-ran the init pass on every element. Visible bug: scroll-anim
	// elements snapped back to their from-state on every lifecycle
	// fire. With the reset removed, `applyPhoneGate` diffs the fresh
	// candidate set against the current active set, and ONLY truly-new
	// elements (or those that just toggled phone-state) get the init
	// pass. Survivors keep their `_timedPhase` / mid-flight progress.
	//
	// Stale elements (gone from the DOM) drop out naturally because
	// they aren't in `allCandidates` anymore; the next time
	// `applyPhoneGate` rebuilds `elements`/`elementConfigs` from
	// `allCandidates`, they're simply not included.
	applyPhoneGate()

	if (prefersReducedMotion) {
		// Reduce-motion: snap every element to its final state and bail.
		elements.forEach((el, index) => {
			const cfg = elementConfigs[index]
			el.style.transform = ''
			el.style.opacity = cfg && cfg.baseOpacity ? cfg.baseOpacity : ''
			el.style.filter = ''
			el.style.clipPath = ''
		})
		return
	}

	console.log('[fp-anim] collectAndObserve done', { elementsCount: elements.length, fpActive: isFullpageActive(), allCandidates: allCandidates.length })

	if (elements.length === 0) {
		return
	}

	attachScrollListeners()
	// Mark "scrolling" briefly on init so the rAF loop runs once and all
	// elements that happen to be near the viewport at page load animate
	// in correctly.
	onScrollOrResize()

	// Race-condition guard: fp may have already initialised (and its
	// `fpAfterRender` already fired with an empty `elements` list) by the
	// time we get here on a page-nav into an fp page. Re-trigger the active
	// container's animations now that elements exist. No-op when fp is not
	// active.
	if (isFullpageActive()) {
		console.log('[fp-anim] collectAndObserve: fp already active → re-trigger active container')
		fpPlayElementsIn(fpFindContainer(0))
	}
}

// ----- fp helpers (used by both the rAF loop's race-condition guard in
// collectAndObserve and by the fp event listeners in i()) ----------------

// fullpage.js (the JS variant of the fullscreen-slider addon) translates
// the section / slide wrapper via CSS transform instead of scrolling the
// window, so no native `scroll` event fires and the rAF loop never wakes up
// to advance progress. Each fp section/slide is a full viewport, so a
// continuous scroll-progress animation doesn't really fit anyway — instead,
// when fp arrives on a section/slide, we play every scroll-anim element
// inside that container as a one-shot timed animation. When the user
// leaves a section/slide, we reset the elements back to their progress=0
// state so re-entering replays the animation. Both vertical (sections) and
// horizontal (slides within a section) modes are covered. CSS scroll-snap
// is unaffected (it uses native scroll, our regular pipeline works for it).
const fpFindContainer = (sectionIndex, slideIndex) => {
	const sections = document.querySelectorAll('.fp-section')
	const section = sections[sectionIndex]
	if (!section) {
		console.log('[fp-anim] fpFindContainer: no section at index', sectionIndex, 'totalSections=', sections.length)
		return null
	}
	if (typeof slideIndex === 'number' && slideIndex >= 0) {
		const slides = section.querySelectorAll('.fp-slide')
		const slide = slides[slideIndex] || section
		console.log('[fp-anim] fpFindContainer: slide path', { sectionIndex, slideIndex, totalSlidesInSection: slides.length, foundSlide: slides[slideIndex] === slide ? 'yes' : 'fallback to section' })
		return slide
	}
	const activeSlide = section.querySelector('.fp-slide.active')
	console.log('[fp-anim] fpFindContainer: section path', { sectionIndex, hasActiveSlide: !!activeSlide, totalSlidesInSection: section.querySelectorAll('.fp-slide').length })
	return activeSlide || section
}

const fpDetectInitialSlide = (sectionIndex) => {
	const sections = document.querySelectorAll('.fp-section')
	const section = sections[sectionIndex]
	if (!section) return null
	const activeSlide = section.querySelector('.fp-slide.active')
	if (!activeSlide) return null
	const slides = section.querySelectorAll('.fp-slide')
	return Array.prototype.indexOf.call(slides, activeSlide)
}

const fpPlayElementsIn = (container) => {
	if (!container) {
		console.log('[fp-anim] fpPlayElementsIn: no container — bail')
		return
	}
	const nowMs = performance.now()
	let matched = 0
	let scrub = 0
	let timed = 0
	let outsideContainer = 0
	const containerTagDbg = container.tagName + '.' + (container.className || '').toString().split(/\s+/).slice(0, 3).join('.')
	for (let i = 0; i < elements.length; i++) {
		const el = elements[i]
		const cfg = elementConfigs[i]
		if (!el || !cfg) continue
		const inside = container.contains(el)
		// Targeted log for the test element so we can see its config + container relation
		const isTestEl = el.getAttribute && el.getAttribute('data-lay-scroll-anim-direction') === 'right' && el.getAttribute('data-lay-scroll-anim-distance') === '100'
		if (isTestEl) {
			console.log('[fp-anim] TEST EL in fpPlayElementsIn', {
				inside,
				containerTag: containerTagDbg,
				cfgScrub: cfg.scrubMode,
				cfgPhase: cfg._timedPhase,
				cfgDuration: cfg.durationFrames,
				directionEnabled: cfg.directionEnabled,
				direction: cfg.direction,
				distancePct: cfg.distancePct,
				elClasses: (el.className || '').toString().slice(0, 80),
				elTransform: el.style.transform,
			})
		}
		if (!inside) {
			outsideContainer++
			continue
		}
		matched++
		if (cfg.scrubMode) {
			scrub++
			// Scrub semantics need continuous scroll progress, which fp
			// can't provide — but the user wants the animation to PLAY
			// when arriving on a slide, not just snap to its end. Run a
			// one-shot tween (progress 0 → 1) using the same elapsed-time
			// math the timed pipeline uses, driven by `_fpScrubPhase` in
			// `tick`. Default to 60 frames (~1s) when no duration was
			// configured (scrub elements typically don't define one).
			const rect = getNaturalViewportRect(el)
			applyEasedProgress(el, cfg, rect, cfg.easingFn(0))
			cfg._fpScrubPhase = 'active'
			cfg._fpScrubStartMs = nowMs
			cfg._fpScrubDurationMs = (cfg.durationFrames > 0 ? cfg.durationFrames : 60) * FRAME_MS
			if (cfg.willChangeStr && el.style.willChange !== cfg.willChangeStr) {
				el.style.willChange = cfg.willChangeStr
			}
		} else {
			timed++
			const groupId = cfg.buildAnimGroupId
			let groupTriggeredAt = groupId ? getBuildAnimGroupTriggered(groupId) : undefined
			if (groupId && groupTriggeredAt == null) {
				groupTriggeredAt = nowMs
				setBuildAnimGroupTriggered(groupId, nowMs)
			}
			cfg._staggerDelayMs = cfg.delayFrames * FRAME_MS
			cfg._timedPhase = cfg._staggerDelayMs > 0 ? 'pending' : 'active'
			cfg._timedStartMs = groupTriggeredAt != null ? groupTriggeredAt : nowMs
			cfg._lastTransform = undefined
			if (cfg.willChangeStr && el.style.willChange !== cfg.willChangeStr) {
				el.style.willChange = cfg.willChangeStr
			}
		}
	}
	console.log('[fp-anim] fpPlayElementsIn:', { totalElements: elements.length, containerTag: containerTagDbg, matched, timed, scrub, outsideContainer })
	// Kick the rAF loop — no scroll events fire under fp.
	onScrollOrResize()
}

const fpResetElementsIn = (container) => {
	if (!container) return
	for (let i = 0; i < elements.length; i++) {
		const el = elements[i]
		const cfg = elementConfigs[i]
		if (!el || !cfg) continue
		if (!container.contains(el)) continue
		if (cfg.scrubMode) {
			cfg._fpScrubPhase = 'idle'
			cfg._fpScrubStartMs = 0
			cfg._lastTransform = undefined
			if (el.style.willChange) el.style.willChange = ''
			const rect = getNaturalViewportRect(el)
			applyEasedProgress(el, cfg, rect, cfg.easingFn(0))
		} else {
			if (cfg.buildAnimGroupId) clearBuildAnimGroupTriggered(cfg.buildAnimGroupId)
			cfg._timedPhase = 'idle'
			cfg._timedStartMs = 0
			cfg._staggerDelayMs = 0
			cfg._lastTransform = undefined
			if (el.style.willChange) el.style.willChange = ''
			const rect = getNaturalViewportRect(el)
			applyEasedProgress(el, cfg, rect, cfg.easingFn(0))
		}
	}
}

// Track the previous active container so we can reset its elements when the
// leave transition COMPLETES (i.e. the slide is fully off-screen), not when
// it starts. fp's `onLeave`/`onSlideLeave` fire at the start of the
// transition while the leaving slide is still visible — resetting then
// would visibly snap content back to its initial state in front of the
// user. Resetting in `afterLoad`/`afterSlideLoad` (= new container fully
// loaded = old one is fully gone) hides the reset behind the off-screen
// viewport.
let fpPrevSectionIndex = null
let fpPrevSlideIndex = null
let fpPrevSlideSectionIndex = null

// ----- public init -------------------------------------------------------

const i = () => {
	const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
	prefersReducedMotion = mq.matches

	collectAndObserve()

	// Re-bind on every Lay AJAX nav (same lifecycle event the imagehover and
	// element-animate-in modules use).
	if (window.laytheme && typeof window.laytheme.on === 'function') {
		window.laytheme.on('newpageshown', () => {
			collectAndObserve()
		})
		// Page overlays exist in the DOM at page load but are hidden — their
		// elements have offsetWidth/Height = 0 at init time, so the initial
		// updateElement pass produces a zero rect and they stay on the
		// (over-aggressive) PHP inline style. When the overlay actually opens
		// and dimensions become real, kick the rAF loop so those elements
		// repaint with the correct progress=0 state instead of waiting for
		// the user to scroll.
		const kick = () => onScrollOrResize()
		window.laytheme.on('overlayshown', kick)
		window.laytheme.on('overlayshown_animation_ended', kick)
		window.laytheme.on('smoothscroll', kick)

		// Page overlays are reused DOM. Reset their scroll-animation elements
		// before the next open, otherwise a previously played element remains
		// painted at progress=1 for one visible frame and then snaps back to
		// progress=0 when the first overlay scroll tick runs.
		const resetOverlayElementsToStart = ($overlayEl, deferUntilOverlayShownEnded) => {
			const overlayNode = $overlayEl && $overlayEl[0] ? $overlayEl[0] : null
			for (let i = 0; i < elements.length; i++) {
				const el = elements[i]
				const cfg = elementConfigs[i]
				if (!el || !cfg) continue
				if (overlayNode && !overlayNode.contains(el)) continue
				cfg._deferUntilOverlayShownEnded = !!deferUntilOverlayShownEnded
				resetElementToStart(el, cfg)
			}
		}
		window.laytheme.on('page_overlay_before_shown', ($overlayEl) => {
			resetOverlayElementsToStart($overlayEl, true)
		})
		window.laytheme.on('overlayshown_animation_ended', ($overlayEl) => {
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					resetOverlayElementsToStart($overlayEl, false)
					const overlayNode = $overlayEl && $overlayEl[0] ? $overlayEl[0] : null
					for (let i = 0; i < elements.length; i++) {
						const el = elements[i]
						const cfg = elementConfigs[i]
						if (!el || !cfg) continue
						if (overlayNode && !overlayNode.contains(el)) continue
						cfg._deferUntilOverlayShownEnded = false
					}
					onScrollOrResize()
				})
			})
		})
		window.laytheme.on('overlayhidden', ($overlayEl) => {
			resetOverlayElementsToStart($overlayEl, false)
		})
	}

	// Thumbnail-grid filter changes hide some items via `display: none`
	// and unhide others. Re-shown items have their phase still in 'done'
	// (or wherever it was before the filter), so they wouldn't animate
	// when they appear. Reset every non-scrub item's phase to 'idle' on
	// filter change — `updateElementTimed` will then re-trigger them
	// based on their current viewport position. We also kick the rAF
	// loop so newly-shown items animate without waiting for the next
	// scroll event.
	if (window.eventsChannel && typeof window.eventsChannel.on === 'function') {
		window.eventsChannel.on('thumbnailGridWasFiltered', () => {
			for (let i = 0; i < elementConfigs.length; i++) {
				const cfg = elementConfigs[i]
				const el = elements[i]
				if (!cfg.scrubMode) {
					cfg._timedPhase = 'idle'
					cfg._timedStartMs = 0
					cfg._staggerDelayMs = 0
					cfg._lastTransform = undefined
					// Items mid-animation might have will-change set; clear it
					// so we don't leave GPU layers dangling. It'll be re-set on
					// the next idle→pending/active transition.
					if (el && el.style.willChange) el.style.willChange = ''
				}
			}
			onScrollOrResize()
		})
	}

	if (mq.addEventListener) {
		mq.addEventListener('change', (e) => {
			prefersReducedMotion = e.matches
			collectAndObserve()
		})
	}

	// Section transitions (vertical mode + horizontal-mode section nav)
	jQuery(window).on('fpAfterLoad', (event, info) => {
		console.log('[fp-anim] fpAfterLoad', info)
		if (!info || typeof info.index !== 'number') return
		const newIdx = info.index - 1
		if (fpPrevSectionIndex !== null && fpPrevSectionIndex !== newIdx) {
			// Previous section just finished animating off-screen — safe to
			// reset its scroll-anim elements without the user seeing the snap.
			const prevSection = document.querySelectorAll('.fp-section')[fpPrevSectionIndex]
			fpResetElementsIn(prevSection)
		}
		fpPlayElementsIn(fpFindContainer(newIdx))
		fpPrevSectionIndex = newIdx
		// Section change implicitly clears any tracked slide on the previous
		// section; track the new section's initial active slide (if any).
		fpPrevSlideIndex = fpDetectInitialSlide(newIdx)
		fpPrevSlideSectionIndex = fpPrevSlideIndex !== null ? newIdx : null
	})

	// Slide transitions (horizontal mode within a section)
	jQuery(window).on('fpAfterSlideLoad', (event, info) => {
		console.log('[fp-anim] fpAfterSlideLoad', info)
		if (!info || typeof info.index !== 'number' || typeof info.slideIndex !== 'number') return
		const sectionIdx = info.index - 1
		const newSlideIdx = info.slideIndex
		if (
			fpPrevSlideIndex !== null &&
			fpPrevSlideSectionIndex === sectionIdx &&
			fpPrevSlideIndex !== newSlideIdx
		) {
			fpResetElementsIn(fpFindContainer(sectionIdx, fpPrevSlideIndex))
		}
		fpPlayElementsIn(fpFindContainer(sectionIdx, newSlideIdx))
		fpPrevSlideIndex = newSlideIdx
		fpPrevSlideSectionIndex = sectionIdx
	})

	// First section/slide is already visible at `afterRender` time but
	// fp doesn't emit afterLoad/afterSlideLoad for it. Play the initial
	// active container at render time + seed the prev-tracking state.
	jQuery(window).on('fpAfterRender', () => {
		console.log('[fp-anim] fpAfterRender — total fp-sections:', document.querySelectorAll('.fp-section').length, 'elementsCount:', elements.length)
		fpPlayElementsIn(fpFindContainer(0))
		fpPrevSectionIndex = 0
		fpPrevSlideIndex = fpDetectInitialSlide(0)
		fpPrevSlideSectionIndex = fpPrevSlideIndex !== null ? 0 : null
	})
}

const scroll_animations_controller = {
	i: i,
}

export default scroll_animations_controller
